// Copyright (c) 2020, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import { compose, graphql } from 'react-apollo'
import gql from 'graphql-tag'
import * as Sentry from '@sentry/browser'
import moment from 'moment'

import {
  Button,
  ExpandableOverviewItem,
  WorkflowRunSteps,
  SvgIcon
} from '../'

import { WORKFLOW_LOAN_DISBURSAL } from '../../constants'

import { decodeBase64 } from '../../utils'


class CreditApplicationDisburse extends PureComponent {
  static propTypes = {
    status: PropTypes.string,
    open: PropTypes.bool.isRequired,
    creditId: PropTypes.string,
    creditWorkflowRunProcessing: PropTypes.shape({
      id: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      atStep: PropTypes.number,
      createdBy: PropTypes.object
    }),
    issuedAt: PropTypes.string,
    onClick: PropTypes.func.isRequired,
    refetchData: PropTypes.func.isRequired,
    className: PropTypes.string
  }

  state = {
    refreshed: null,
    refreshInProgress: false,
    disburseInProgress: false,
    disburseInitiateFailed: false,
    issuedAtElapsedMilliSeconds: null
  }

  componentDidMount() {
    if (this.props.creditWorkflowRunProcessing === null) {
      // if no disbursal workflow has been saved on credit, get/set elapsed seconds since issue
      this.setState({ issuedAtElapsedMilliSeconds: moment().diff(this.props.issuedAt) })
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.refreshInProgress && this.state.refreshInProgress === false) {
      this.setState({
        refreshed: true,
        issuedAtElapsedMilliSeconds: moment().diff(this.props.issuedAt)
      })
    }
  }

  getSummary = (disbursalWorkflowExists, finalisedApplication, issuedBy) => (
    disbursalWorkflowExists
      ? finalisedApplication
        ? 'Finalised application'
        : !issuedBy
          ? 'Loans not issued'
          : ''
      : 'No loan disbursal workflow'
  )

  getIconSymbol = (disbursalWorkflowExists, disbursalWorkflowProcessed) => (
    disbursalWorkflowExists
      ? disbursalWorkflowProcessed ? 'check' : null
      : 'warning'
  )

  getIconColour = (disbursalWorkflowExists, disbursalWorkflowProcessed) => (
    disbursalWorkflowExists
      ? disbursalWorkflowProcessed ? 'success' : null
      : 'warning'
  )

  initiateLoanDisbursalWorkflowRun = () => {
    let input = {
      workflow: WORKFLOW_LOAN_DISBURSAL,
      version: 1,
      status: 'pending',
      parameters: JSON.stringify({ credit: decodeBase64(this.props.creditId) })
    }

    return this.props.initiateLoanDisbursalWorkflowRun({ variables: { input } })
  }

  disburse = () => {
    this.setState({ disburseInProgress: true }, () => {
      // initiate disbursal workflow
      this.initiateLoanDisbursalWorkflowRun()
      .then(() => {
        setTimeout(
          () => {
            this.setState(
              { disburseInProgress: false, refreshed: null, disburseInitiateFailed: false },
              this.props.refetchData
            )
          },
          5000  // 5 seconds
        )
      })
      .catch(error => {
        Sentry.captureMessage(`Failed to initiate loan disbursal workflow - ${error}`)
        this.setState({ disburseInProgress: false, refreshed: null, disburseInitiateFailed: true })
      })
    })
  }

  render() {
    const { status, issuedBy, open, creditWorkflowRunProcessing, onClick, className } = this.props
    const { disburseInProgress, refreshed, refreshInProgress, disburseInitiateFailed, issuedAtElapsedMilliSeconds } = this.state
    const issueComplete = issuedBy !== null
    const finalisedApplication = status === 'FINALISED_APPLICATION'
    const disbursalWorkflowProcessed = creditWorkflowRunProcessing && creditWorkflowRunProcessing.status === 'PROCESSED'
    const disbursalWorkflowExists = creditWorkflowRunProcessing !== null

    return (
      <ExpandableOverviewItem
        title='Disburse and activate'
        summary={this.getSummary(disbursalWorkflowExists, finalisedApplication, issuedBy)}
        iconSymbol={this.getIconSymbol(disbursalWorkflowExists, disbursalWorkflowProcessed)}
        iconColour={this.getIconColour(disbursalWorkflowExists, disbursalWorkflowProcessed)}
        open={open}
        locked={!issueComplete}
        onClick={onClick}
        className={className}>

        {open && disbursalWorkflowExists
          ? <div className='slds-m-left_small slds-m-top_small slds-m-bottom_medium'>
              <div className='slds-m-bottom_x-small u-font-size-large slds-text-color_weak'>
                Loan activation status:
              </div>
              <WorkflowRunSteps workflowRunId={creditWorkflowRunProcessing.id} className='slds-scrollable_y' />
            </div>
          : <div className='slds-m-left_small slds-m-top_small slds-m-bottom_medium'>
              {refreshed !== null && disbursalWorkflowExists === false && issuedAtElapsedMilliSeconds > 30000  // 30 sec
                // if refreshed and disbursal workflow still doesn't exist after 30 seconds since credit issued
                ? <span>
                    <Button
                      type='brand'
                      minWidth={90}
                      disabled={disburseInProgress}
                      onClick={this.disburse}
                      className='slds-m-left_small slds-m-top_small slds-m-bottom_medium'>

                      {disburseInProgress
                        ? 'Initiating...'
                        : 'Initiate disbursal'
                      }
                    </Button>
                  </span>
                : <span>
                    <SvgIcon
                      variant='utility'
                      symbol={disburseInitiateFailed ? 'warning' : 'info'}
                      size='small'
                      colour={disburseInitiateFailed ? 'error' : 'light'}
                      className='slds-m-right_x-small'
                    />
                    No disbursal workflow currently exists.
                    <Button
                      type='reset'
                      onClick={() => {
                        this.setState({ refreshInProgress: true }, this.props.refetchData)
                        setTimeout(
                          () => this.setState({ refreshInProgress: false }),
                          2000  // 2 sec timeout to allow for "Refreshing..." button feedback to user
                        )
                      }}
                      className='slds-m-left_xx-small'>
                      {refreshInProgress
                        ? 'Refreshing...'
                        : 'Refresh'
                      }
                    </Button>
                  </span>
              }

            </div>
        }
      </ExpandableOverviewItem>
    )
  }
}

const initiateLoanDisbursalWorkflowRun = gql`
  mutation ($input: WorkflowRunMutationInput!) {
    workflowrunMutate(input: $input) {
      workflowrun {
        id
      }
    }
  }
`

const withWorkflowRunMutate = graphql(
  initiateLoanDisbursalWorkflowRun, { name: "initiateLoanDisbursalWorkflowRun"}
)

export default compose(
  withWorkflowRunMutate,
  withRouter
)(CreditApplicationDisburse)



// WEBPACK FOOTER //
// ./src/components/creditapplication-disburse/index.js